import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdvisorNotesForm = ({ caseId, agent, onUpdate }) => {
  const [notes, setNotes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newNote, setNewNote] = useState({ note_date: new Date().toISOString().slice(0, 10), note: '', note_type: 'general' });
  const [aiReview, setAiReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/service-notes`);
      if (r.ok) setNotes(await r.json());
    } catch (e) { console.error('Failed to fetch service notes:', e); }
    // Also fetch any saved AI review for this case
    try {
      const r2 = await fetch(`${API_URL}/api/compliance/cases/${caseId}/notes/ai-review`);
      if (r2.ok) setAiReview(await r2.json());
    } catch (e) { console.error('Failed to fetch AI review:', e); }
  }, [caseId]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const add = async () => {
    if (!newNote.note.trim()) { alert('Note cannot be empty'); return; }
    const res = await fetch(`${API_URL}/api/compliance/cases/${caseId}/service-notes?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newNote)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert('Failed to save note: ' + (err.detail || res.statusText));
      return;
    }
    setShowAdd(false);
    setNewNote({ note_date: new Date().toISOString().slice(0, 10), note: '', note_type: 'general' });
    fetchNotes();
    onUpdate && onUpdate();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this advisor note? This cannot be undone.')) return;
    const res = await fetch(`${API_URL}/api/compliance/cases/${caseId}/service-notes/${id}?advisor_id=${agent.id}`, { method: 'DELETE' });
    if (res.ok) { fetchNotes(); onUpdate && onUpdate(); }
  };

  const runAiReview = async () => {
    setAiLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/ai-advisor-notes-review?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'AI review failed');
      }
      const j = await r.json();
      setAiReview({ summary: j.summary, observations: j.observations, flags: j.flags });
      onUpdate && onUpdate();
    } catch (e) {
      alert('AI review failed: ' + e.message);
    } finally { setAiLoading(false); }
  };

  const flagColor = (flag) => {
    const k = (flag || '').split(':')[0].toUpperCase();
    return ({
      AFFORDABILITY: 'bg-amber-100 text-amber-900 border-amber-300',
      COVERAGE: 'bg-rose-100 text-rose-900 border-rose-300',
      REPLACEMENT: 'bg-orange-100 text-orange-900 border-orange-300',
      DATA: 'bg-blue-100 text-blue-900 border-blue-300',
      SUITABILITY: 'bg-purple-100 text-purple-900 border-purple-300',
      AML: 'bg-red-100 text-red-900 border-red-300',
      REGULATORY: 'bg-red-100 text-red-900 border-red-300',
    })[k] || 'bg-gray-100 text-gray-900 border-gray-300';
  };

  return (
    <Card data-testid="advisor-notes-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="sr-only">Advisor Notes</CardTitle>
        <div className="flex gap-2 ml-auto">
          <Button onClick={runAiReview} disabled={aiLoading} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-advisor-notes-review-btn">
            <Sparkles className="h-4 w-4 mr-1" />{aiLoading ? 'Reviewing…' : 'AI Review'}
          </Button>
          <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-[#0077B6]" data-testid="add-advisor-note-btn">
            <Plus className="h-4 w-4 mr-1" />Add Note
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {aiReview && (
          <div className="border-2 border-[#D4AF37] rounded-lg p-4 bg-yellow-50 space-y-2" data-testid="ai-review-block">
            <div className="flex items-center gap-2 text-[#003D5C] font-semibold">
              <Sparkles className="h-4 w-4" /> AI Review
            </div>
            {aiReview.summary && (
              <p className="text-sm text-gray-800 italic">{aiReview.summary}</p>
            )}
            {Array.isArray(aiReview.flags) && aiReview.flags.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Flags requiring attention</div>
                {aiReview.flags.map((f, i) => (
                  <div key={`flag-${i}-${(f || '').slice(0, 20)}`} className={`text-sm border rounded px-2 py-1 ${flagColor(f)}`}>{f}</div>
                ))}
              </div>
            )}
            {Array.isArray(aiReview.observations) && aiReview.observations.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Observations</div>
                <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                  {aiReview.observations.map((o, i) => <li key={`obs-${i}-${(o || '').slice(0, 20)}`}>{o}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {showAdd && (
          <div className="border rounded-lg p-4 bg-blue-50 space-y-2">
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">Date</label>
                <input type="date" value={newNote.note_date} onChange={(e) => setNewNote({ ...newNote, note_date: e.target.value })} className="w-full px-2 py-2 border rounded" data-testid="an-date" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Category</label>
                <select value={newNote.note_type} onChange={(e) => setNewNote({ ...newNote, note_type: e.target.value })} className="w-full px-2 py-2 border rounded" data-testid="an-category">
                  <option value="general">General</option>
                  <option value="annual_review">Annual Review</option>
                  <option value="policy_anniversary">Policy Anniversary</option>
                  <option value="beneficiary_review">Beneficiary Review</option>
                  <option value="coverage_review">Coverage Review</option>
                  <option value="life_event">Life Event</option>
                </select>
              </div>
            </div>
            <textarea rows="4" value={newNote.note} onChange={(e) => setNewNote({ ...newNote, note: e.target.value })} placeholder="Your note..." className="w-full px-2 py-2 border rounded" data-testid="an-notes" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={add} className="bg-[#0077B6]" data-testid="save-advisor-note-btn">Save Note</Button>
            </div>
          </div>
        )}
        {notes.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No advisor notes yet.</p>
        ) : (
          <div className="space-y-2">
            {notes.map(n => (
              <div key={n.id} className="border rounded-lg p-3" data-testid={`advisor-note-${n.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{n.note_date} — {(n.note_type || n.category || 'note').replace(/_/g, ' ')}</span>
                  <Button size="sm" variant="ghost" onClick={() => remove(n.id)} title="Delete" data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.note || n.notes}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// =============================================================
// Reason Why Letter (AI-generated, post-application)
// =============================================================
